/**
 * Space Icon Emoji
 *
 * Normalizes user and Losi-provided icon hints into emoji.
 *
 * Primary responsibilities:
 * - Preserve explicit emoji icons
 * - Convert common text icon hints to matching emoji
 *
 * Dependencies:
 * - JavaScript Unicode property escapes
 */

type IconAlias = {
  emoji: string
  terms: readonly string[]
}

const EMOJI_PATTERN = /\p{Extended_Pictographic}\uFE0F?/u

const ICON_ALIASES: readonly IconAlias[] = [
  { emoji: '📥', terms: ['inbox'] },
  { emoji: '💡', terms: ['idea', 'ideas', 'lightbulb', 'concept'] },
  { emoji: '✅', terms: ['task', 'tasks', 'done', 'complete', 'completed', 'checklist', 'check', 'todo', 'to do'] },
  { emoji: '🚀', terms: ['execution', 'execute', 'launch', 'rocket', 'ship'] },
  { emoji: '📅', terms: ['calendar', 'schedule', 'scheduled', 'event', 'events', 'date', 'meeting', 'meetings'] },
  { emoji: '📝', terms: ['note', 'notes', 'doc', 'docs', 'document', 'writing'] },
  { emoji: '📊', terms: ['sheet', 'spreadsheet', 'csv', 'xlsx', 'analytics', 'chart', 'report', 'reports'] },
  { emoji: '📄', terms: ['file', 'pdf', 'attachment', 'asset', 'assets'] },
  { emoji: '🔎', terms: ['research', 'search', 'discover', 'audit'] },
  { emoji: '📈', terms: ['pipeline', 'growth', 'trend', 'metrics'] },
  { emoji: '💼', terms: ['business', 'work', 'briefcase'] },
  { emoji: '🔧', terms: ['tool', 'tools', 'wrench'] },
  { emoji: '💬', terms: ['chat', 'message', 'conversation', 'comment'] },
  { emoji: '🤖', terms: ['ai', 'assistant', 'automation', 'bot', 'losi'] },
  { emoji: '🤝', terms: ['partner', 'partners', 'partnership', 'deal'] },
  { emoji: '📣', terms: ['marketing', 'campaign', 'announcement'] },
  { emoji: '💰', terms: ['money', 'revenue', 'sales', 'monetization', 'budget'] },
  { emoji: '👥', terms: ['team', 'people', 'users', 'members', 'customer', 'customers'] },
  { emoji: '👤', terms: ['contact', 'contacts', 'person', 'profile'] },
  { emoji: '🎯', terms: ['goal', 'target', 'objective'] },
  { emoji: '⚙️', terms: ['settings', 'config', 'configuration', 'ops'] },
  { emoji: '🔒', terms: ['private', 'security', 'secure', 'lock'] },
  { emoji: '🌐', terms: ['public', 'web', 'global', 'internet'] },
  { emoji: '⭐', terms: ['star', 'favorite', 'important'] },
  { emoji: '🛟', terms: ['support', 'help', 'service'] },
  { emoji: '🎨', terms: ['design', 'creative', 'palette'] },
  { emoji: '💻', terms: ['code', 'dev', 'development', 'engineering'] },
  { emoji: '🐛', terms: ['bug', 'issue', 'fix'] },
  { emoji: '📁', terms: ['folder', 'folders', 'space', 'project', 'workspace'] }
] as const

function normalizeIconText(value: string) {
  return value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^\p{Letter}\p{Number}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function hasAliasMatch(normalizedValue: string, term: string) {
  if (term.includes(' ')) {
    return ` ${normalizedValue} `.includes(` ${term} `)
  }

  return normalizedValue.split(' ').includes(term)
}

export function normalizeSpaceEmojiIcon(
  value: string | null | undefined,
  fallback: string | null = null
) {
  const rawValue = value?.trim()

  if (!rawValue) return fallback

  const explicitEmoji = rawValue.match(EMOJI_PATTERN)?.[0]
  if (explicitEmoji) return explicitEmoji

  const normalizedValue = normalizeIconText(rawValue)
  if (!normalizedValue) return fallback

  const match = ICON_ALIASES.find(({ terms }) =>
    terms.some(term => hasAliasMatch(normalizedValue, term))
  )

  return match?.emoji ?? fallback
}

// --------------

export function formatSpaceNameWithIcon(
  name: string | null | undefined,
  icon: string | null | undefined,
  fallback: string | null = "📁"
) {
  const label = name?.trim() || "Untitled space"
  const normalizedIcon = normalizeSpaceEmojiIcon(icon, fallback)

  return normalizedIcon ? `${normalizedIcon} ${label}` : label
}

/** Main chat list title for a Space-scoped chat: `{emoji} / {chatName}`. */
export function formatSpaceChatListTitle(
  chatName: string | null | undefined,
  spaceIcon: string | null | undefined,
  untitledFallback = 'Untitled chat'
) {
  const label = chatName?.trim() || untitledFallback
  const emoji = normalizeSpaceEmojiIcon(spaceIcon)

  return emoji ? `${emoji} / ${label}` : label
}
